<template>
  <div class="relative">
    <poppins-notification
      id="profile-link-toast"
      class="-mt-16"
      message="Sitter profile link copied to clipboard."
      :triggerNotification="showToast"
      @toggleVisibility="closeToast"
    />
    <div class="flex justify-between items-center">
      <poppins-breadcrumb :header="breadcrumbInfo.header" class="mt-0" :href="breadcrumbInfo.link" anchorLink />
      <div v-if="accountTypeInfo.isParent" class="flex gap-8">
        <provider-profile-favorite-sitter-button
          :favoritedSitter="favoritedSitter"
          :sitterAccountId="sitterAccountId"
          class="self-center"
          @favoritedSitterChange="favoritedSitterChange"
        />
        <action-menu :menuOptions="kebabOptions" iconType="kebab" noBackground />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import kebabCase from 'lodash/kebabCase';
import PoppinsBreadcrumb from '@/components/breadcrumb/Breadcrumb.vue';
import ActionMenu from '@/components/action_menu/ActionMenu.vue';
import PoppinsNotification from '@/components/notification/Notification.vue';
import ProviderProfileFavoriteSitterButton from './ProviderProfileFavoriteSitterButton.vue';

import { MenuItemOptions } from '@/components/action_menu/__types__/ActionMenuTypes';

import WarningOutlineIcon from '@/graphics/icons/WarningOutlineIcon.vue';
import ShareIcon from '@/graphics/icons/ShareIcon.vue';

export default {
  name: 'ProviderProfileActionBar',
  components: {
    PoppinsBreadcrumb,
    ActionMenu,
    PoppinsNotification,
    ProviderProfileFavoriteSitterButton,
  },
  props: {
    accountTypeInfo: {
      default: () => ({}),
      type: Object,
    },
    profileAccountData: {
      default: () => ({}),
      type: Object,
    },
    viewerAccountData: {
      default: () => ({}),
      type: Object,
    },
    favoritedSitter: {
      default: false,
      type: Boolean,
    },
  },
  data() {
    return {
      showToast: false,
    };
  },
  computed: {
    sitterAccountId(): string {
      return this.profileAccountData?.id || '';
    },
    parentZipcode(): string {
      return this.viewerAccountData?.zipcode || '';
    },
    favoritedLabel(): string {
      if (this.favoritedSitter) {
        return 'Unfavorite sitter';
      }
      return 'Favorite sitter';
    },
    caretypeLink(): string {
      // babysitter & pet sitting links are different than the care type name + require special cases
      if (this.$route.params.care_type === 'babysitter') {
        return `/babysitters`;
      }
      if (this.$route.params.care_type === 'pet_care') {
        return `/pet-sitting`;
      }
      // assuming care_type is companion_care, special_needs, nanny, housekeeping
      return `/${kebabCase(this.$route.params.care_type)}`;
    },
    kebabOptions(): MenuItemOptions[] {
      return [
        {
          icon: WarningOutlineIcon,
          height: 24,
          width: 24,
          labelText: 'Report sitter',
          href: `https://support.sittercity.com/hc/en-us/requests/new?ticket_form_id=360000078194&account_id=${this.sitterAccountId}`,
          target: '_blank',
          click: () => {},
        },
        {
          icon: ShareIcon,
          height: 24,
          width: 24,
          labelText: 'Share profile',
          closeOnClick: true,
          click: () => {
            navigator.clipboard.writeText(window.location.toString());
            this.showToast = true;
            setTimeout(() => {
              document.getElementById('profile-link-toast').focus();
              // eslint-disable-next-line no-multi-assign
              document.body.scrollTop = document.documentElement.scrollTop = 0;
            }, 1);
          },
        },
      ];
    },
    breadcrumbInfo(): any {
      if (!this.accountTypeInfo?.accountType) {
        return {
          header: 'More',
          link: this.caretypeLink,
        };
      }
      if (this.accountTypeInfo.isParent) {
        return {
          header: 'Back',
          link: `/availability/search?careType=${this.$route.params.care_type}&zipcode=${this.parentZipcode}`,
        };
      }
      if (this.accountTypeInfo.isSitter) {
        return {
          header: 'Dashboard',
          link: `/account/providers/dashboard`,
        };
      }
      return null;
    },
  },
  methods: {
    favoritedSitterChange(favoritedSitter) {
      this.$emit('favoritedSitterChange', favoritedSitter);
    },
    closeToast() {
      this.showToast = false;
    },
  },
};
</script>
